import { useReglages } from '../../api/reglages.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { Inp } from '../../components/ui/Inputs.jsx';
import { distLisible } from '../../lib/geo';
import { tr } from '../../lib/i18n';
import {
  CLE_LIEUX_TRAVAIL,
  RAYONS,
  coordonnee,
  libelleLieu,
  lieuxDeLaSociete,
  BUREAU,
} from '../../lib/lieux-travail';
import { C } from '../../theme/palette';
import { Constat, Etiquette, Puces } from './Puces.jsx';

/** Relève la position du téléphone, ou prévient qu'elle est introuvable. */
function relevePosition(quand) {
  const refusee = () =>
    window.alert(
      tr(
        'Position introuvable : autorise la localisation pour SwissTech dans les réglages du téléphone, puis réessaie.',
      ),
    );
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    refusee();
    return;
  }
  navigator.geolocation.getCurrentPosition(
    (po) => quand({ lat: coordonnee(po.coords.latitude), lng: coordonnee(po.coords.longitude) }),
    refusee,
  );
}

/** La position d'un lieu : enregistrée (avec « redéfinir ») ou à enregistrer. */
function Position({ position, onPosition }) {
  if (position) {
    return (
      <>
        <Constat ton="ok">{tr('✓ Position enregistrée')}</Constat>
        <Btn
          label={tr('🔄 Redéfinir avec ma position actuelle')}
          sm
          ghost
          color={C.navy}
          full
          onClick={() => relevePosition(onPosition)}
        />
      </>
    );
  }
  return (
    <Btn
      label={tr('📍 Enregistrer avec ma position actuelle')}
      sm
      color={C.navy}
      full
      onClick={() => relevePosition(onPosition)}
    />
  );
}

/**
 * Réglages → Société → Adresses de travail : le bureau (l'adresse de la
 * société), le dépôt, et la distance acceptée pour pointer « sur place ». Les
 * positions s'enregistrent debout sur place, comme pour un chantier : l'appli
 * ne cherche pas les adresses sur une carte.
 *
 * @param {object} props propriétés
 * @param {object} props.soc la société (adresse du bureau)
 * @returns {import('react').ReactElement} la carte
 */
export function AdressesTravailCard({ soc }) {
  const { reglageJson, setReglageJson } = useReglages();
  const brut = reglageJson(CLE_LIEUX_TRAVAIL, {}) || {};
  const lieux = lieuxDeLaSociete(brut);
  const change = (champs) => setReglageJson(CLE_LIEUX_TRAVAIL, { ...brut, ...champs });
  const adresseBureau = [soc?.adr, soc?.npa].filter(Boolean).join(', ');

  return (
    <Card style={{ marginBottom: 12 }}>
      <Bar icon="📍" title={tr('Adresses de travail')} color={C.teal} />
      <div style={{ padding: 13 }}>
        <Etiquette>{libelleLieu(BUREAU)}</Etiquette>
        <div
          style={{
            border: '1px solid ' + C.border,
            borderRadius: 9,
            padding: '10px',
            fontSize: 11,
            marginBottom: 8,
            color: adresseBureau ? C.ink : C.muted,
          }}
        >
          {adresseBureau || '—'}
        </div>
        <Position position={lieux.bureau} onPosition={(p) => change({ bureau: p })} />
        <div style={{ fontSize: 9, color: C.muted, margin: '6px 0 14px' }}>
          {tr('Reprise de l’adresse de la société.')}
        </div>

        <Etiquette>{tr('📦 Dépôt')}</Etiquette>
        <Inp
          value={lieux.depotAdresse}
          onChange={(e) => change({ depotAdresse: e.target.value })}
          placeholder={tr('ex. Route du Dépôt 5, 1228 Plan-les-Ouates')}
        />
        <Position position={lieux.depot} onPosition={(p) => change({ depot: p })} />
        <div style={{ fontSize: 9, color: C.muted, margin: '6px 0 14px', lineHeight: 1.5 }}>
          {tr('À faire une fois, sur place. Sans dépôt : laisser vide.')}
        </div>

        <Etiquette>{tr('📏 Distance acceptée')}</Etiquette>
        <Puces
          titre={tr('📏 Distance acceptée')}
          options={RAYONS.map((r) => [r, distLisible(r)])}
          valeur={lieux.rayon}
          onChange={(r) => change({ rayon: r })}
        />
        <div style={{ fontSize: 9, color: C.muted, lineHeight: 1.5 }}>
          {tr('Plus loin, l’heure est quand même notée, avec « hors du lieu ».')}
        </div>
      </div>
    </Card>
  );
}
